// Captures console.log/info/warn/error output and ships it to the server so
// support can see what happened on a specific restaurant's device without
// asking staff to open devtools — mainly for debugging intermittent
// auto-print (WebUSB/Bluetooth) failures remotely. Off by default: only
// active while an admin has flipped `console_logs_enabled` on for this
// partner (see foo-server-v2 SettingsService.isConsoleLoggingEnabledForPartner).
//
// Deliberately implemented with plain fetch() instead of Apollo — this
// module is initialized once at app startup, before React/Apollo Client
// exist, and stays fully self-contained so wrapping console.* can't
// introduce a dependency cycle with the app's own GraphQL setup.

type LogLevel = "log" | "info" | "warn" | "error";

const FLUSH_INTERVAL_MS = 15 * 1000; // how often to upload buffered entries
const MAX_BUFFER_SIZE = 500; // hard cap so a runaway loop can't grow this unbounded
const MAX_ARG_STRING_LENGTH = 2000;

type BufferedEntry = {
  level: LogLevel;
  message: string;
  meta: string | null;
  branch_id: number | null;
};

let buffer: BufferedEntry[] = [];
let enabled = false;
let started = false;

function graphUrl(): string {
  return process.env.REACT_APP_GRAPH_URL as string;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return {
    "content-type": "application/json",
    authorization: token ? `Bearer ${token}` : "",
  };
}

function activeBranchId(): number | null {
  try {
    const settings = JSON.parse(localStorage.getItem("settings") || "{}");
    return settings.active_branch ? Number(settings.active_branch) : null;
  } catch {
    return null;
  }
}

function stringifyArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg === undefined) return "undefined";
  if (arg instanceof Error) {
    // Error objects serialize to "{}" via JSON.stringify (message/stack
    // aren't own enumerable properties) — console.error(err) is one of the
    // most common calls this feature exists to capture, so losing the
    // message would defeat the point.
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ""}`;
  }
  try {
    // JSON.stringify returns undefined (not a string) for functions and a
    // few other non-serializable values, not just for circular refs that
    // throw — fall back to String() for those too, not just the catch case.
    const json = JSON.stringify(arg);
    return json === undefined ? String(arg) : json;
  } catch {
    return String(arg);
  }
}

async function graphqlRequest<T>(
  query: string,
  variables: Record<string, unknown>,
): Promise<T | null> {
  try {
    const res = await fetch(graphUrl(), {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) return null;
    return json.data as T;
  } catch {
    return null;
  }
}

function recordEntry(level: LogLevel, args: unknown[]) {
  if (!enabled) return;
  if (buffer.length >= MAX_BUFFER_SIZE) return;

  const message = args
    .map((arg) => stringifyArg(arg).slice(0, MAX_ARG_STRING_LENGTH))
    .join(" ");
  const meta = args.length > 1 ? JSON.stringify(args.slice(1)).slice(0, MAX_ARG_STRING_LENGTH) : null;

  buffer.push({
    level,
    message: message.slice(0, MAX_ARG_STRING_LENGTH),
    meta,
    branch_id: activeBranchId(),
  });
}

const ORIGINAL_CONSOLE: Record<LogLevel, (...args: unknown[]) => void> = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

function wrapConsole() {
  (["log", "info", "warn", "error"] as LogLevel[]).forEach((level) => {
    console[level] = (...args: unknown[]) => {
      ORIGINAL_CONSOLE[level](...args);
      recordEntry(level, args);
    };
  });
}

async function flush() {
  if (buffer.length === 0) return;
  const entries = buffer;
  buffer = [];

  const ok = await graphqlRequest<{ pushConsoleLogs: boolean }>(
    `mutation PushConsoleLogs($entries: [ConsoleLogInput!]!) {
      pushConsoleLogs(entries: $entries)
    }`,
    { entries },
  );

  // On failure, put the entries back (bounded) rather than losing them
  // silently — a transient network blip shouldn't drop a debugging session.
  if (!ok) {
    buffer = [...entries, ...buffer].slice(-MAX_BUFFER_SIZE);
  }
}

async function checkEnabledState() {
  const data = await graphqlRequest<{ getConsoleLogsEnabled: boolean }>(
    `query GetConsoleLogsEnabled { getConsoleLogsEnabled }`,
    {},
  );
  if (data === null) return; // logged out, network error, etc. — leave state as-is
  enabled = data.getConsoleLogsEnabled;
}

export function initConsoleCapture() {
  if (started) return;
  started = true;

  wrapConsole();

  // Checked once per page load only (no polling interval, by design — this
  // is a support-driven debugging toggle, not something that needs to react
  // live in an already-open tab. When support needs it on, they're on a call
  // with the restaurant and can ask for a refresh).
  checkEnabledState();
  setInterval(flush, FLUSH_INTERVAL_MS);

  // Best-effort flush before the tab actually goes away — a 15s interval
  // alone would lose whatever was buffered in the final stretch of a session
  // (e.g. logs right around the print failure the admin is trying to see).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", () => flush());
}
